import React, { useCallback, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { Alert, Button, Collapse, FormList, Notification, Select, Tooltip, useForm } from '@bringg/react-components';
import Modal from '@bringg/react-components/dist/components/modal/modal';
import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';
import { observer } from 'mobx-react-lite';
import { AllowedEmailDomainsEnforcementLevel } from '@bringg/types';
// eslint-disable-next-line no-restricted-imports
import { Form } from 'antd';
import type { Rule } from 'antd/lib/form';
import {
	BRINGG_INTERNAL_DOMAIN,
	DOMAIN_REGEX,
	getEmailDomainNormalized,
	isEmailDomainMatching
} from '@bringg-frontend/utils';

import useStores from '../../../recipes/use-stores';
import { UserDomainGroups } from './components/user-domain-groups';
import { DomainInputRow } from './components/domain-input-row';
import { SuggestedDomains } from './components/suggested-domains';

import styles from './allowed-email-domains.module.scss';
const MAX_DOMAINS = 15;
const MAX_SUGGESTED_DOMAINS = 5;
const DEFAULT_ACTIVE_KEY = ['allowed-email-domains'];

interface DomainsFormValues {
	domains: string[];
}

const getNormalizedDomains = (domains: string[]): string[] => {
	return [...new Set(domains.map(d => d.trim().toLowerCase()).filter(Boolean))];
};

const AllowedEmailDomains: React.FC = observer(() => {
	const { t } = useTranslation();
	const { merchantConfigurationsStore, driversStore } = useStores();

	const { allowedEmailDomains: serverDomains, allowedEmailDomainsEnforcementLevel: serverEnforcementLevel } =
		merchantConfigurationsStore;

	const [form] = useForm<DomainsFormValues>();
	const [saving, setSaving] = useState(false);
	const [showUserEmails, setShowUserEmails] = useState(false);
	const [isDirty, setIsDirty] = useState(false);
	const [hasErrors, setHasErrors] = useState(false);
	const [enforcementLevel, setEnforcementLevel] =
		useState<AllowedEmailDomainsEnforcementLevel>(serverEnforcementLevel);
	const [showConfirmModal, setShowConfirmModal] = useState(false);

	const domains: string[] = Form.useWatch('domains', form) ?? serverDomains;
	const isMaxDomains = domains.length >= MAX_DOMAINS;

	const checkDirty = useCallback(
		(currentDomains: string[], currentEnforcementLevel: AllowedEmailDomainsEnforcementLevel) => {
			if (currentEnforcementLevel !== serverEnforcementLevel) {
				setIsDirty(true);
				return;
			}
			const normalized = currentDomains.map(d => (d ?? '').trim().toLowerCase()).filter(Boolean);
			if (normalized.length !== serverDomains.length) {
				setIsDirty(true);
				return;
			}
			const sorted = [...normalized].sort();
			const serverSorted = [...serverDomains].map(d => d.toLowerCase()).sort();
			setIsDirty(sorted.some((v, i) => v !== serverSorted[i]));
		},
		[serverDomains, serverEnforcementLevel]
	);

	const updateErrorState = useCallback(() => {
		const fieldErrors = form.getFieldsError();
		setHasErrors(fieldErrors.some(f => f.errors.length > 0));
	}, [form]);

	const handleEnforcementLevelChange = (value: AllowedEmailDomainsEnforcementLevel) => {
		setEnforcementLevel(value);
		const currentDomains: string[] = form.getFieldValue('domains');
		checkDirty(currentDomains, value);
	};

	const enforcementLevelOptions = [
		{
			id: AllowedEmailDomainsEnforcementLevel.AdminsOnly,
			name: t('SECURITY.ALLOWED_EMAIL_DOMAINS.ENFORCEMENT_LEVEL_ADMINS_ONLY')
		},
		{
			id: AllowedEmailDomainsEnforcementLevel.AdminsAndDispatchers,
			name: t('SECURITY.ALLOWED_EMAIL_DOMAINS.ENFORCEMENT_LEVEL_ADMINS_AND_DISPATCHERS')
		}
	];

	const handleValuesChange = useCallback(
		(_: Partial<DomainsFormValues>, allValues: DomainsFormValues) => {
			checkDirty(allValues.domains ?? [], enforcementLevel);
			updateErrorState();
		},
		[checkDirty, updateErrorState, enforcementLevel]
	);

	const handleFieldsChange = useCallback(() => {
		updateErrorState();
	}, [updateErrorState]);

	const getDomainRules = useCallback(
		(fieldIndex: number): Rule[] => [
			{ required: true, message: t('SECURITY.ALLOWED_EMAIL_DOMAINS.EMPTY_DOMAIN') },
			{
				validator(_, value: string) {
					const trimmed = value?.trim();
					if (!trimmed) return Promise.resolve();
					if (!DOMAIN_REGEX.test(trimmed)) {
						return Promise.reject(new Error(t('SECURITY.ALLOWED_EMAIL_DOMAINS.INVALID_DOMAIN_FORMAT')));
					}
					const allDomains: string[] = form.getFieldValue('domains');
					const duplicate = allDomains.some(
						(d, i) => i !== fieldIndex && d?.trim().toLowerCase() === trimmed.toLowerCase()
					);
					if (duplicate) {
						return Promise.reject(new Error(t('SECURITY.ALLOWED_EMAIL_DOMAINS.DUPLICATE_DOMAIN')));
					}
					return Promise.resolve();
				}
			}
		],
		[form, t]
	);

	const affectedUsers =
		enforcementLevel === AllowedEmailDomainsEnforcementLevel.AdminsAndDispatchers
			? driversStore.allAdminsAndDispatchers
			: driversStore.admins;

	const usersOutsideAllowedDomains = useMemo(() => {
		if (!affectedUsers.length) return [];

		const errors = form.getFieldsError();
		const errorIndices = new Set(errors.filter(f => f.errors.length > 0).map(f => f.name[1] as number));

		const validDomains = domains
			.filter((d, i) => !errorIndices.has(i) && d?.trim() && DOMAIN_REGEX.test(d.trim()))
			.map(d => d.trim().toLowerCase());

		if (!validDomains.length) return [];

		const isEmailDomainAllowedOrInternal = (emailDomain: string): boolean => {
			if (emailDomain === BRINGG_INTERNAL_DOMAIN) return true;
			return validDomains.some(d => isEmailDomainMatching(emailDomain, d));
		};

		return affectedUsers.filter(user => {
			const emailDomain = getEmailDomainNormalized(user.email);
			return emailDomain && !isEmailDomainAllowedOrInternal(emailDomain);
		});
	}, [affectedUsers, domains, form]);

	const suggestedDomains = useMemo(() => {
		if (!affectedUsers.length) return [];

		const domainCounts = new Map<string, number>();
		affectedUsers.forEach(user => {
			const emailDomain = getEmailDomainNormalized(user.email);
			if (!emailDomain || emailDomain === BRINGG_INTERNAL_DOMAIN) return;
			domainCounts.set(emailDomain, (domainCounts.get(emailDomain) ?? 0) + 1);
		});

		const validConfiguredDomains = domains
			.map(d => (d ?? '').trim().toLowerCase())
			.filter(d => d && DOMAIN_REGEX.test(d));

		return Array.from(domainCounts.entries())
			.filter(([domain]) => !validConfiguredDomains.some(configured => isEmailDomainMatching(domain, configured)))
			.sort((a, b) => b[1] - a[1])
			.slice(0, MAX_SUGGESTED_DOMAINS)
			.map(([domain]) => domain);
	}, [affectedUsers, domains]);

	const usersGroupedByDomain = useMemo(() => {
		const grouped = new Map<string, Bringg.User[]>();
		usersOutsideAllowedDomains.forEach(user => {
			const emailDomain = getEmailDomainNormalized(user.email) || '';
			const domainUsers = grouped.get(emailDomain);

			if (domainUsers) {
				domainUsers.push(user);
			} else {
				grouped.set(emailDomain, [user]);
			}
		});
		return Array.from(grouped.entries()).sort(([a], [b]) => a.localeCompare(b));
	}, [usersOutsideAllowedDomains]);

	const isRemovingDomainsInUse = useMemo(() => {
		if (!affectedUsers.length || serverDomains.length === 0) return false;

		const normalizedCurrentDomains = domains.map(d => (d ?? '').trim().toLowerCase()).filter(Boolean);
		const removedDomains = serverDomains.filter(domain => !normalizedCurrentDomains.includes(domain.toLowerCase()));

		if (removedDomains.length === 0) return false;

		const isEmailMatchingRemovedDomain = (emailDomain: string): boolean => {
			return removedDomains.some(d => isEmailDomainMatching(emailDomain, d));
		};

		return affectedUsers.some(user => {
			const emailDomain = getEmailDomainNormalized(user.email);
			return emailDomain && emailDomain !== BRINGG_INTERNAL_DOMAIN && isEmailMatchingRemovedDomain(emailDomain);
		});
	}, [affectedUsers, domains, serverDomains]);

	const save = useCallback(async () => {
		const currentDomains: string[] = form.getFieldValue('domains');
		const normalizedDomains = getNormalizedDomains(currentDomains);

		setSaving(true);
		try {
			await merchantConfigurationsStore.updateAllowedEmailDomains(normalizedDomains, enforcementLevel);
			Notification.success(t('SECURITY.ALLOWED_EMAIL_DOMAINS.SAVE_SUCCESS'));
			setIsDirty(false);
		} catch {
			Notification.error(t('SECURITY.ALLOWED_EMAIL_DOMAINS.SAVE_ERROR'));
		} finally {
			setSaving(false);
		}
	}, [form, merchantConfigurationsStore, t, enforcementLevel]);

	const validateAndSave = useCallback(async () => {
		try {
			await form.validateFields();
		} catch {
			return;
		}

		if (usersOutsideAllowedDomains.length > 0) {
			setShowConfirmModal(true);
			return;
		}

		await save();
	}, [form, usersOutsideAllowedDomains.length, save]);

	const handleConfirmSave = async () => {
		setShowConfirmModal(false);
		await save();
	};

	const handleAddSuggestedDomain = useCallback(
		(domain: string) => {
			const currentDomains: string[] = form.getFieldValue('domains') ?? [];
			if (currentDomains.length >= MAX_DOMAINS) return;
			const updated = [...currentDomains, domain];
			form.setFieldsValue({ domains: updated });
			checkDirty(updated, enforcementLevel);
		},
		[form, checkDirty, enforcementLevel]
	);

	const handleDiscard = useCallback(() => {
		form.setFieldsValue({ domains: [...serverDomains] });
		setEnforcementLevel(serverEnforcementLevel);
		setIsDirty(false);
		setHasErrors(false);
	}, [form, serverDomains, serverEnforcementLevel]);

	return (
		<div className={styles.allowedEmailDomains} data-test-id="allowed-email-domains">
			<Modal
				visible={showConfirmModal}
				title={t('SECURITY.ALLOWED_EMAIL_DOMAINS.SAVE_CONFIRM_TITLE')}
				okText={t('SECURITY.ALLOWED_EMAIL_DOMAINS.SAVE_CONFIRM_OK')}
				cancelText={t('SECURITY.ALLOWED_EMAIL_DOMAINS.SAVE_CONFIRM_CANCEL')}
				onOk={handleConfirmSave}
				onCancel={() => setShowConfirmModal(false)}
				okButtonProps={{ danger: true }}
				data-test-id="save-confirm-modal"
			>
				{t('SECURITY.ALLOWED_EMAIL_DOMAINS.SAVE_CONFIRM_BODY', {
					count: usersOutsideAllowedDomains.length
				})}
			</Modal>
			<Form
				form={form}
				initialValues={{ domains: [...serverDomains] }}
				onValuesChange={handleValuesChange}
				onFieldsChange={handleFieldsChange}
			>
				<Collapse
					expandIconPosition="end"
					defaultActiveKey={DEFAULT_ACTIVE_KEY}
					removeHeaderPadding
					removeContentPadding
				>
					<Collapse.Panel
						key="allowed-email-domains"
						header={
							<div className={styles.domainHeader}>
								<span className={styles.domainTitle}>{t('SECURITY.ALLOWED_EMAIL_DOMAINS.TITLE')}</span>
								<span className={styles.domainSummary}>
									{t('SECURITY.ALLOWED_EMAIL_DOMAINS.SUMMARY')}
								</span>
							</div>
						}
					>
						<div className={styles.domainContent}>
							<div className={styles.enforcementLevelRow}>
								<span className={styles.enforcementLevelLabel}>
									{t('SECURITY.ALLOWED_EMAIL_DOMAINS.ENFORCEMENT_LEVEL_LABEL')}
								</span>
								<Select
									value={enforcementLevel}
									onChange={handleEnforcementLevelChange}
									className={styles.enforcementLevelSelect}
									data-test-id="enforcement-level-select"
									options={enforcementLevelOptions}
								/>
							</div>
							<FormList name="domains">
								{(fields, { add, remove }) => (
									<>
										{fields.map((field, index) => (
											<DomainInputRow
												key={field.key}
												index={index}
												fieldName={field.name}
												placeholder={t('SECURITY.ALLOWED_EMAIL_DOMAINS.DOMAIN_PLACEHOLDER')}
												rules={getDomainRules(index)}
												onRemove={() => {
													remove(field.name);
													const updated = form.getFieldValue('domains');
													checkDirty(updated, enforcementLevel);
												}}
											/>
										))}

										{suggestedDomains.length > 0 && !isMaxDomains && (
											<SuggestedDomains
												domains={suggestedDomains}
												onAdd={domain => {
													handleAddSuggestedDomain(domain);
												}}
											/>
										)}

										<div className={styles.domainActionsRow}>
											<Tooltip
												title={
													isMaxDomains
														? t('SECURITY.ALLOWED_EMAIL_DOMAINS.MAX_DOMAINS_REACHED')
														: ''
												}
												placement="bottomLeft"
												getPopupContainer={trigger => trigger.parentElement || document.body}
											>
												<span>
													<Button
														type="link"
														className={styles.addDomainBtn}
														disabled={isMaxDomains}
														onClick={() => add('')}
														icon={<BringgIcon iconName={BringgFontIcons.Plus} />}
														data-test-id="add-domain-btn"
													>
														{t('SECURITY.ALLOWED_EMAIL_DOMAINS.ADD_DOMAIN')}
													</Button>
												</span>
											</Tooltip>

											<div className={styles.domainActions}>
												<Button
													disabled={!isDirty}
													onClick={handleDiscard}
													data-test-id="discard-btn"
												>
													{t('SECURITY.ALLOWED_EMAIL_DOMAINS.DISCARD')}
												</Button>
												<Tooltip
													title={
														isRemovingDomainsInUse
															? t(
																	'SECURITY.ALLOWED_EMAIL_DOMAINS.CANNOT_REMOVE_DOMAINS_IN_USE'
															  )
															: ''
													}
													placement="bottomRight"
													getPopupContainer={trigger =>
														trigger.parentElement || document.body
													}
												>
													<span>
														<Button
															type="primary"
															disabled={!isDirty || hasErrors || isRemovingDomainsInUse}
															loading={saving}
															onClick={validateAndSave}
															data-test-id="save-btn"
														>
															{t('SECURITY.ALLOWED_EMAIL_DOMAINS.SAVE')}
														</Button>
													</span>
												</Tooltip>
											</div>
										</div>
									</>
								)}
							</FormList>

							{usersOutsideAllowedDomains.length > 0 && (
								<div className={styles.usersWarningWrapper} data-test-id="users-outside-warning">
									<Alert
										type="warning"
										message={t('SECURITY.ALLOWED_EMAIL_DOMAINS.USERS_OUTSIDE_WARNING', {
											count: usersOutsideAllowedDomains.length
										})}
										showIcon
										className={styles.usersWarning}
									/>
									<Button
										type="link"
										size="small"
										className={styles.toggleUserListBtn}
										onClick={() => setShowUserEmails(!showUserEmails)}
										data-test-id="toggle-user-list-btn"
									>
										{showUserEmails
											? t('SECURITY.ALLOWED_EMAIL_DOMAINS.HIDE_DETAILS')
											: t('SECURITY.ALLOWED_EMAIL_DOMAINS.SHOW_DETAILS')}
									</Button>
									{showUserEmails && (
										<div
											className={styles.userEmailListContainer}
											data-test-id="user-email-list-container"
										>
											<UserDomainGroups usersGroupedByDomain={usersGroupedByDomain} />
										</div>
									)}
								</div>
							)}
						</div>
					</Collapse.Panel>
				</Collapse>
			</Form>
		</div>
	);
});

export default AllowedEmailDomains;
